import clientStorage from "../../services/clientStorage";
import { LANGUAGE_PREFERENCE_KEY, LANGUAGES } from "./constants";
import LocalizationCache from "./core/cache";
import { fetchLocalizationPackage } from "./core/fetcher";
import { i18nEngine } from "./core/i18nEngine";
import resolveLanguage from "./core/resolveLanguage";
import localizationLogger from "./localizationLogger";
import { SupportedLanguage } from "./types";
class LocalizationManager {
    isInitializedState = false;
    subscribers = new Set();
    languageChangeQueue = Promise.resolve();
    appId = "";
    persistentStorage = clientStorage;
    localizationCache = new LocalizationCache(clientStorage);
    resolveLocalResources;
    appConfig = null;
    reportMissingKeyCallback;
    reportedMissingTranslationKeyIds = new Set();
    get isInitialized() {
        return this.isInitializedState;
    }
    async init(config) {
        this.appId = config.appId;
        this.resolveLocalResources = config.getLocalResources;
        this.reportMissingKeyCallback = config.reportMissingKey;
        this.reportedMissingTranslationKeyIds.clear();
        const language = resolveLanguage(config.preferredLanguage);
        i18nEngine.init({ language, missingKeyHandler: this.onMissingKey });
        try {
            if (language !== SupportedLanguage.EN) {
                this.loadLanguageResources(SupportedLanguage.EN).catch((error) => {
                    localizationLogger.error("Failed to load English fallback:", error);
                });
            }
            await this.loadLanguageResources(language);
        }
        catch (error) {
            localizationLogger.error(`Failed to fetch localization for ${language}, falling back to bundled resources:`, error);
        }
        await this.persistLanguagePreference(language);
        this.isInitializedState = true;
        this.notifySubscribers();
    }
    async setAppConfig(localizationConfig) {
        this.appConfig = this.parseAppConfig(localizationConfig);
        const currentLang = this.getCurrentLanguage();
        const latestVersion = this.appConfig[currentLang];
        if (!latestVersion)
            return;
        const cachedVersion = await this.localizationCache.getVersion(currentLang);
        if (!cachedVersion || Number(cachedVersion) < Number(latestVersion)) {
            void this.refreshLocalizationPackage();
        }
    }
    setCurrentLanguage = (language) => {
        const next = this.languageChangeQueue.then(() => this.doSetLanguage(language));
        this.languageChangeQueue = next.catch((error) => {
            localizationLogger.error(`Failed to set language to ${language}:`, error);
        });
        return next;
    };
    async refreshLocalizationPackage() {
        const currentLang = this.getCurrentLanguage();
        try {
            if (currentLang !== SupportedLanguage.EN) {
                this.loadLanguageResources(SupportedLanguage.EN).catch((error) => {
                    localizationLogger.error("Failed to load English fallback:", error);
                });
            }
            await this.loadLanguageResources(currentLang);
        }
        catch (error) {
            localizationLogger.error("Failed to refresh localization package:", error);
        }
    }
    getCurrentLanguage() {
        return i18nEngine.getCurrentLanguage();
    }
    getSupportedLanguages() {
        return Object.values(LANGUAGES);
    }
    async removeLanguageResources({ language }) {
        i18nEngine.removeLanguageBundles(language);
        await this.localizationCache.remove(language);
    }
    getLoadedLanguages() {
        return i18nEngine.getLoadedLanguages();
    }
    // eslint-disable-next-line id-length -- Standard i18n API convention; renaming would confuse consumers familiar with i18next.
    t(key, options) {
        // eslint-disable-next-line custom/no-dynamic-i18n-key -- Shared localization passthrough; callers must pass literal namespace:key strings so static extractors see them at call sites.
        return i18nEngine.t(key, options);
    }
    subscribe = (listener) => {
        this.subscribers.add(listener);
        return () => this.subscribers.delete(listener);
    };
    // --- Private ---
    async persistLanguagePreference(language) {
        try {
            await this.persistentStorage.setItem(LANGUAGE_PREFERENCE_KEY, language);
        }
        catch (persistError) {
            localizationLogger.error(`Failed to persist the language preference "${language}":`, persistError);
        }
    }
    async doSetLanguage(language) {
        const currentLang = this.getCurrentLanguage();
        if (currentLang === language)
            return;
        await this.loadLanguageResources(language);
        if (currentLang !== SupportedLanguage.EN) {
            await this.removeLanguageResources({ language: currentLang });
        }
        await this.persistLanguagePreference(language);
        await i18nEngine.changeLanguage(language);
        if (language !== SupportedLanguage.EN) {
            this.loadLanguageResources(SupportedLanguage.EN).catch((error) => {
                localizationLogger.error("Failed to load English fallback:", error);
            });
        }
    }
    async loadLanguageResources(language) {
        const timestamp = this.appConfig?.[language] ?? null;
        const cachedPackage = await this.localizationCache.get(language);
        const translations = await this.resolveTranslations({
            language,
            timestamp,
            cachedPackage,
        });
        i18nEngine.addFlatResources(language, translations);
        this.loadLocalResources(language);
    }
    async resolveTranslations({ language, timestamp, cachedPackage, }) {
        if (cachedPackage && this.isCacheFresh({ cachedVersion: cachedPackage.version, timestamp })) {
            return cachedPackage.translations;
        }
        try {
            const fetched = await this.fetchPackage({ language, timestamp });
            const versionToStore = timestamp ?? String(Date.now());
            await this.localizationCache.set(language, versionToStore, fetched);
            return fetched;
        }
        catch (error) {
            if (cachedPackage)
                return cachedPackage.translations;
            throw error;
        }
    }
    parseAppConfig(config) {
        return Object.fromEntries(Object.entries(config).map(([lang, query]) => [lang, new URLSearchParams(query).get("timestamp") ?? ""]));
    }
    isCacheFresh({ cachedVersion, timestamp }) {
        if (!timestamp)
            return cachedVersion !== null;
        if (!cachedVersion)
            return false;
        return Number(cachedVersion) >= Number(timestamp);
    }
    async fetchPackage({ language, timestamp }) {
        return fetchLocalizationPackage({
            appId: this.appId,
            language,
            timestamp,
        });
    }
    loadLocalResources(language) {
        if (!this.resolveLocalResources)
            return;
        const localResources = this.resolveLocalResources(language);
        if (localResources && Object.keys(localResources).length > 0) {
            i18nEngine.addFlatResources(language, localResources);
        }
    }
    onMissingKey = (languages, namespace, key, fallbackValue) => {
        if (!this.isInitializedState)
            return;
        localizationLogger.warn(`Missing translation key "${key}" in namespace "${namespace}" (current language: ${this.getCurrentLanguage()}, fallback chain: [${languages.join(", ")}], app: ${this.appId})`);
        const reportMissingKey = this.reportMissingKeyCallback;
        if (!reportMissingKey)
            return;
        const missingTranslationKeyId = `${languages.join(",")}|${namespace}|${key}`;
        if (this.reportedMissingTranslationKeyIds.has(missingTranslationKeyId))
            return;
        this.reportedMissingTranslationKeyIds.add(missingTranslationKeyId);
        try {
            reportMissingKey({
                languages,
                namespace,
                key,
                fallbackValue,
                currentLanguage: this.getCurrentLanguage(),
                appId: this.appId,
            });
        }
        catch {
            // Reporter failures must not affect translation resolution.
        }
    };
    notifySubscribers() {
        this.subscribers.forEach((listener) => listener());
    }
}
export const localization = new LocalizationManager();
export default localization;
